import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  message, Tabs, Tag, Space, Popconfirm, Descriptions, Row, Col,
  Statistic, Progress, Divider, Tooltip, Alert, Typography
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  CheckOutlined, CloseOutlined, DollarCircleOutlined,
  PieChartOutlined, BarChartOutlined, FileTextOutlined,
  RiseOutlined, BookOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Project, ProjectRevenue, ProjectRevenueStats, ProjectFinancialSummary,
  ProjectRevenueCreate, ProjectRevenueUpdate, RevenueType, RevenueStatus
} from '@/types';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

interface ProjectWithRevenues extends Project {
  revenueSummary?: ProjectFinancialSummary | null;
}

interface ProjectRevenuesState {
  projects: ProjectWithRevenues[];
  selectedProject: ProjectWithRevenues | null;
  revenues: ProjectRevenue[];
  revenueStats: ProjectRevenueStats | null;
  financialSummary: ProjectFinancialSummary | null;
  loading: boolean;
  revenueModalVisible: boolean;
  revenueDetailModalVisible: boolean;
  selectedRevenue: ProjectRevenue | null;
  editingRevenue: ProjectRevenue | null;
  revenueForm: any;
}

const ProjectRevenues: React.FC = () => {
  const { user } = useAuth();
  const [revenueForm] = Form.useForm();
  
  const [state, setState] = useState<ProjectRevenuesState>({
    projects: [],
    selectedProject: null,
    revenues: [],
    revenueStats: null,
    financialSummary: null,
    loading: false,
    revenueModalVisible: false,
    revenueDetailModalVisible: false,
    selectedRevenue: null,
    editingRevenue: null,
    revenueForm
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (state.selectedProject) {
      loadProjectRevenues(state.selectedProject.id);
      loadFinancialSummary(state.selectedProject.id);
      loadRevenueStats(state.selectedProject.id);
    }
  }, [state.selectedProject]);

  const loadProjects = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const projects = await api.getCostAccessibleProjects();
      setState(prev => ({ 
        ...prev, 
        projects,
        selectedProject: projects[0] || null,
        loading: false 
      }));
    } catch (error) {
      message.error('加载项目列表失败');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const loadProjectRevenues = async (projectId: number) => {
    try {
      const response = await api.getProjectRevenues({ project_id: projectId, limit: 100 });
      setState(prev => ({ ...prev, revenues: response.revenues }));
    } catch (error) {
      message.error('加载收入记录失败');
    }
  };

  const loadFinancialSummary = async (projectId: number) => {
    try {
      const summary = await api.getProjectFinancialSummary(projectId);
      setState(prev => ({ ...prev, financialSummary: summary }));
    } catch (error) {
      console.error('加载财务汇总失败', error);
    }
  };

  const loadRevenueStats = async (projectId: number) => {
    try {
      const stats = await api.getProjectRevenueStats(projectId);
      setState(prev => ({ ...prev, revenueStats: stats }));
    } catch (error) {
      console.error('加载收入统计失败', error);
    }
  };

  const handleCreateRevenue = async (values: any) => {
    try {
      const revenueData: ProjectRevenueCreate = {
        project_id: state.selectedProject!.id,
        amount: values.amount,
        revenue_date: values.revenue_date.format('YYYY-MM-DD'),
        description: values.description,
        revenue_type: values.revenue_type,
        status: values.status
      };
      
      await api.createProjectRevenue(revenueData);
      message.success('收入记录创建成功');
      setState(prev => ({ ...prev, revenueModalVisible: false }));
      revenueForm.resetFields();
      
      if (state.selectedProject) {
        await loadProjectRevenues(state.selectedProject.id);
        await loadFinancialSummary(state.selectedProject.id);
        await loadRevenueStats(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '创建收入记录失败');
    }
  };

  const handleUpdateRevenue = async (values: any) => {
    try {
      const updateData: ProjectRevenueUpdate = {
        amount: values.amount,
        revenue_date: values.revenue_date.format('YYYY-MM-DD'),
        description: values.description,
        revenue_type: values.revenue_type,
        status: values.status
      };
      
      await api.updateProjectRevenue(state.editingRevenue!.id, updateData);
      message.success('收入记录更新成功');
      setState(prev => ({ ...prev, revenueModalVisible: false, editingRevenue: null }));
      revenueForm.resetFields();
      
      if (state.selectedProject) {
        await loadProjectRevenues(state.selectedProject.id);
        await loadFinancialSummary(state.selectedProject.id);
        await loadRevenueStats(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '更新收入记录失败');
    }
  };

  const handleDeleteRevenue = async (revenueId: number) => {
    try {
      await api.deleteProjectRevenue(revenueId);
      message.success('收入记录删除成功');
      
      if (state.selectedProject) {
        await loadProjectRevenues(state.selectedProject.id);
        await loadFinancialSummary(state.selectedProject.id);
        await loadRevenueStats(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '删除收入记录失败');
    }
  };

  const handleConfirmRevenue = async (revenueId: number) => {
    try {
      await api.confirmProjectRevenue(revenueId);
      message.success('收入记录确认成功');
      
      if (state.selectedProject) {
        await loadProjectRevenues(state.selectedProject.id);
        await loadFinancialSummary(state.selectedProject.id);
        await loadRevenueStats(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '确认收入记录失败');
    }
  };

  const handleCancelRevenue = async (revenueId: number) => {
    try {
      await api.cancelProjectRevenue(revenueId);
      message.success('收入记录取消成功');
      
      if (state.selectedProject) {
        await loadProjectRevenues(state.selectedProject.id);
        await loadFinancialSummary(state.selectedProject.id);
        await loadRevenueStats(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '取消收入记录失败');
    }
  };

  const openRevenueModal = (revenue?: ProjectRevenue) => {
    if (revenue) {
      setState(prev => ({ ...prev, editingRevenue: revenue }));
      revenueForm.setFieldsValue({
        amount: revenue.amount,
        revenue_date: dayjs(revenue.revenue_date),
        description: revenue.description,
        revenue_type: revenue.revenue_type,
        status: revenue.status
      });
    } else {
      setState(prev => ({ ...prev, editingRevenue: null }));
      revenueForm.resetFields();
      revenueForm.setFieldsValue({
        revenue_type: 'contract_payment',
        status: 'pending',
        revenue_date: dayjs()
      });
    }
    setState(prev => ({ ...prev, revenueModalVisible: true }));
  };

  const openRevenueDetail = (revenue: ProjectRevenue) => {
    setState(prev => ({ 
      ...prev, 
      selectedRevenue: revenue, 
      revenueDetailModalVisible: true 
    }));
  };

  const getRevenueTypeText = (type: RevenueType) => {
    const typeMap = {
      contract_payment: '合同付款',
      milestone_payment: '里程碑付款',
      additional_service: '增值服务',
      other: '其他'
    };
    return typeMap[type] || type;
  };

  const getRevenueStatusTag = (status: RevenueStatus) => {
    const statusMap = {
      pending: { color: 'orange', text: '待确认' },
      confirmed: { color: 'green', text: '已确认' },
      cancelled: { color: 'red', text: '已取消' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const revenueColumns: ColumnsType<ProjectRevenue> = [
    {
      title: '收入金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `¥${amount.toLocaleString()}`,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: '收入日期',
      dataIndex: 'revenue_date',
      key: 'revenue_date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a, b) => dayjs(a.revenue_date).unix() - dayjs(b.revenue_date).unix(),
    },
    {
      title: '收入类型',
      dataIndex: 'revenue_type',
      key: 'revenue_type',
      render: (type: RevenueType) => getRevenueTypeText(type),
      filters: [
        { text: '合同付款', value: 'contract_payment' },
        { text: '里程碑付款', value: 'milestone_payment' },
        { text: '增值服务', value: 'additional_service' },
        { text: '其他', value: 'other' },
      ],
      onFilter: (value: any, record) => record.revenue_type === value,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: RevenueStatus) => getRevenueStatusTag(status),
      filters: [
        { text: '待确认', value: 'pending' },
        { text: '已确认', value: 'confirmed' },
        { text: '已取消', value: 'cancelled' },
      ],
      onFilter: (value: any, record) => record.status === value,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: { showTitle: false },
      render: (description: string) => (
        <Tooltip placement="topLeft" title={description}>
          {description || '-'}
        </Tooltip>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openRevenueDetail(record)}
          >
            详情
          </Button>
          {(user?.role === 'admin' || user?.role === 'supervisor' || 
            state.selectedProject?.manager_id === user?.id) && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openRevenueModal(record)}
              >
                编辑
              </Button>
              {record.status === 'pending' && user?.role === 'admin' && (
                <Popconfirm
                  title="确认此收入记录？"
                  onConfirm={() => handleConfirmRevenue(record.id)}
                  okText="确认"
                  cancelText="取消"
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<CheckOutlined />}
                    style={{ color: 'green' }}
                  >
                    确认
                  </Button>
                </Popconfirm>
              )}
              {record.status !== 'cancelled' && (
                <Popconfirm
                  title="取消此收入记录？"
                  onConfirm={() => handleCancelRevenue(record.id)}
                  okText="确认"
                  cancelText="取消"
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<CloseOutlined />}
                    danger
                  >
                    取消
                  </Button>
                </Popconfirm>
              )}
              <Popconfirm
                title="确认删除此收入记录？"
                onConfirm={() => handleDeleteRevenue(record.id)}
                okText="确认"
                cancelText="取消"
              >
                <Button
                  type="link"
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                >
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  const renderFinancialSummary = () => {
    if (!state.financialSummary) return null;

    const { financialSummary: summary } = state;

    return (
      <Card 
        title={
          <Space>
            <BookOutlined />
            财务汇总
          </Space>
        }
        className="mb-4"
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic 
              title="已确认收入" 
              value={summary.confirmed_revenue} 
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="待确认收入" 
              value={summary.pending_revenue} 
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="总成本" 
              value={summary.total_cost} 
              precision={2}
              prefix="¥"
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="净利润" 
              value={summary.net_profit} 
              precision={2}
              prefix="¥"
              valueStyle={{ 
                color: summary.net_profit >= 0 ? '#3f8600' : '#cf1322' 
              }}
            />
          </Col>
        </Row>
        
        <Divider />
        
        <Row gutter={16}>
          <Col span={6}>
            <Statistic 
              title="毛利润" 
              value={summary.gross_profit} 
              precision={2}
              prefix="¥"
              valueStyle={{ 
                color: summary.gross_profit >= 0 ? '#3f8600' : '#cf1322' 
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="净利润率" 
              value={summary.profit_margin} 
              precision={2}
              suffix="%"
              valueStyle={{ 
                color: summary.profit_margin >= 0 ? '#3f8600' : '#cf1322' 
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="毛利润率" 
              value={summary.gross_profit_margin} 
              precision={2}
              suffix="%"
              valueStyle={{ 
                color: summary.gross_profit_margin >= 0 ? '#3f8600' : '#cf1322' 
              }}
            />
          </Col>
          <Col span={6}>
            <div>
              <div style={{ marginBottom: 8 }}>预算执行率</div>
              <Progress 
                percent={Math.min(summary.budget_usage_rate, 100)} 
                status={summary.budget_usage_rate > 100 ? 'exception' : 'normal'}
                format={(percent) => `${summary.budget_usage_rate.toFixed(1)}%`}
              />
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderRevenueStats = () => {
    if (!state.revenueStats) return null;

    const { revenueStats: stats } = state;

    return (
      <Card 
        title={
          <Space>
            <PieChartOutlined />
            收入统计
          </Space>
        }
        className="mb-4"
      >
        <Row gutter={16}>
          <Col span={8}>
            <Statistic 
              title="总收入记录" 
              value={stats.revenue_count}
              suffix="条"
            />
            <div style={{ marginTop: 8 }}>
              <Tag color="green">已确认: {stats.confirmed_count}</Tag>
              <Tag color="orange">待确认: {stats.pending_count}</Tag>
              <Tag color="red">已取消: {stats.cancelled_count}</Tag>
            </div>
          </Col>
          <Col span={8}>
            <Statistic 
              title="收入金额分布" 
              value={stats.confirmed_revenue} 
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#3f8600' }}
            />
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              <div>待确认: ¥{stats.pending_revenue.toLocaleString()}</div>
              <div>已取消: ¥{stats.cancelled_revenue.toLocaleString()}</div>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <div style={{ marginBottom: 8 }}>收入类型分布</div>
              {Object.entries(stats.by_type).map(([type, amount]) => (
                <div key={type} style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: '12px' }}>
                    {getRevenueTypeText(type as RevenueType)}: ¥{amount.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <RiseOutlined className="mr-2" />
          项目收入管理
        </Title>
      </div>

      {/* 项目选择 */}
      <Card className="mb-4">
        <Row gutter={16} align="middle">
          <Col span={4}>
            <strong>选择项目:</strong>
          </Col>
          <Col span={12}>
            <Select
              style={{ width: '100%' }}
              placeholder="选择要管理的项目"
              value={state.selectedProject?.id}
              onChange={(projectId) => {
                const project = state.projects.find(p => p.id === projectId);
                setState(prev => ({ ...prev, selectedProject: project || null }));
              }}
              loading={state.loading}
            >
              {state.projects.map(project => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            {state.selectedProject && (
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openRevenueModal()}
                  disabled={!state.selectedProject}
                >
                  添加收入记录
                </Button>
              </Space>
            )}
          </Col>
        </Row>
      </Card>

      {state.selectedProject && (
        <div>
          {/* 财务汇总 */}
          {renderFinancialSummary()}

          {/* 收入统计 */}
          {renderRevenueStats()}

          {/* 收入记录列表 */}
          <Card
            title={
              <Space>
                <DollarCircleOutlined />
                收入记录
              </Space>
            }
          >
            <Table
              columns={revenueColumns}
              dataSource={state.revenues}
              rowKey="id"
              loading={state.loading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </div>
      )}

      {/* 收入记录表单模态框 */}
      <Modal
        title={state.editingRevenue ? '编辑收入记录' : '添加收入记录'}
        open={state.revenueModalVisible}
        onCancel={() => {
          setState(prev => ({ ...prev, revenueModalVisible: false, editingRevenue: null }));
          revenueForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={revenueForm}
          layout="vertical"
          onFinish={state.editingRevenue ? handleUpdateRevenue : handleCreateRevenue}
        >
          <Form.Item
            label="收入金额"
            name="amount"
            rules={[
              { required: true, message: '请输入收入金额' },
              { type: 'number', min: 0.01, message: '金额必须大于0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入收入金额"
              precision={2}
              min={0.01}
              max={100000000}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/¥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            label="收入日期"
            name="revenue_date"
            rules={[{ required: true, message: '请选择收入日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="收入类型"
            name="revenue_type"
            rules={[{ required: true, message: '请选择收入类型' }]}
          >
            <Select placeholder="选择收入类型">
              <Option value="contract_payment">合同付款</Option>
              <Option value="milestone_payment">里程碑付款</Option>
              <Option value="additional_service">增值服务</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="选择状态">
              <Option value="pending">待确认</Option>
              <Option value="confirmed">已确认</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
          >
            <TextArea
              placeholder="请输入收入记录描述（可选）"
              rows={3}
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {state.editingRevenue ? '更新' : '创建'}
              </Button>
              <Button onClick={() => {
                setState(prev => ({ ...prev, revenueModalVisible: false, editingRevenue: null }));
                revenueForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 收入记录详情模态框 */}
      <Modal
        title="收入记录详情"
        open={state.revenueDetailModalVisible}
        onCancel={() => setState(prev => ({ ...prev, revenueDetailModalVisible: false, selectedRevenue: null }))}
        footer={[
          <Button key="close" onClick={() => 
            setState(prev => ({ ...prev, revenueDetailModalVisible: false, selectedRevenue: null }))
          }>
            关闭
          </Button>
        ]}
        width={600}
      >
        {state.selectedRevenue && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="收入金额">
              ¥{state.selectedRevenue.amount.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="收入日期">
              {dayjs(state.selectedRevenue.revenue_date).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="收入类型">
              {getRevenueTypeText(state.selectedRevenue.revenue_type)}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getRevenueStatusTag(state.selectedRevenue.status)}
            </Descriptions.Item>
            <Descriptions.Item label="描述">
              {state.selectedRevenue.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(state.selectedRevenue.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {dayjs(state.selectedRevenue.updated_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ProjectRevenues;